// LogbookMemories - "An diesem Tag" Erinnerungen aus vergangenen Jahren

import React, { useMemo } from 'react';
import { Clock, MapPin, Sparkles, Calendar } from 'lucide-react';
import type { PlaceNote } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { getMoodByValue } from '../../constants/moods';

interface LogbookMemoriesProps {
  notes: PlaceNote[];
  onNavigateToPlace?: (placeId: string) => void;
  onViewNote?: (note: PlaceNote) => void;
}

interface MemoryItem {
  note: PlaceNote;
  yearsAgo: number;
  date: Date;
}

function getDateFromTimestamp(date: Timestamp | Date | undefined): Date {
  if (!date) return new Date(0);
  if (date instanceof Timestamp) return date.toDate();
  return new Date(date);
}

export function LogbookMemories({
  notes,
  onNavigateToPlace,
  onViewNote,
}: LogbookMemoriesProps) {
  const today = new Date();

  // Finde Einträge vom gleichen Tag in vergangenen Jahren
  const memories = useMemo(() => {
    const result: MemoryItem[] = [];
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    notes.forEach((note) => {
      const noteDate = getDateFromTimestamp(note.visitDate || note.createdAt);
      const noteYear = noteDate.getFullYear();
      const noteMonth = noteDate.getMonth();
      const noteDay = noteDate.getDate();

      // Gleicher Tag/Monat, aber nicht aktuelles Jahr
      if (
        noteMonth === currentMonth &&
        noteDay === currentDay &&
        noteYear < currentYear
      ) {
        result.push({
          note,
          yearsAgo: currentYear - noteYear,
          date: noteDate,
        });
      }
    });

    // Sortiere nach Jahren (neueste zuerst)
    return result.sort((a, b) => a.yearsAgo - b.yearsAgo);
  }, [notes, today]);

  if (memories.length === 0) {
    return null; // Keine Erinnerungen für heute
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500">
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="w-5 h-5" />
          <h3 className="font-semibold">An diesem Tag</h3>
        </div>
        <p className="text-purple-100 text-sm mt-1">
          Deine Erinnerungen vom {today.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Erinnerungen */}
      <div className="p-4 space-y-3">
        {memories.map((memory) => (
          <MemoryCard
            key={memory.note.id}
            memory={memory}
            onNavigateToPlace={onNavigateToPlace}
            onViewNote={onViewNote}
          />
        ))}
      </div>
    </div>
  );
}

interface MemoryCardProps {
  memory: MemoryItem;
  onNavigateToPlace?: (placeId: string) => void;
  onViewNote?: (note: PlaceNote) => void;
}

function MemoryCard({ memory, onNavigateToPlace, onViewNote }: MemoryCardProps) {
  const { note, yearsAgo, date } = memory;

  // Mood
  const primaryMood = note.moods?.[0] ? getMoodByValue(note.moods[0]) :
                      note.mood ? getMoodByValue(note.mood) : null;

  // Foto
  const photo = note.photoMetadata?.[0]?.thumbnailUrl || note.photoMetadata?.[0]?.url;

  // Jahre-Text
  const yearsText = yearsAgo === 1 ? 'Vor einem Jahr' : `Vor ${yearsAgo} Jahren`;

  return (
    <button
      onClick={() => onViewNote?.(note)}
      className="w-full text-left bg-white rounded-lg border border-purple-100 overflow-hidden hover:shadow-md hover:border-purple-200 transition-all"
    >
      <div className="flex">
        {/* Foto */}
        {photo && (
          <div className="flex-shrink-0 w-24 h-24">
            <img
              src={photo}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-3 min-w-0">
          {/* Jahre-Badge */}
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              <Clock className="w-3 h-3" />
              {yearsText}
            </span>
            {primaryMood && (
              <span className="text-lg" title={primaryMood.label}>
                {primaryMood.emoji}
              </span>
            )}
          </div>

          {/* Ort */}
          <div className="flex items-center gap-1 text-sm text-indigo-600 mb-1">
            <MapPin className="w-3.5 h-3.5" />
            <span className="font-medium truncate">{note.placeName}</span>
          </div>

          {/* Text-Vorschau */}
          <p className="text-sm text-gray-600 line-clamp-2">{note.text}</p>

          {/* Bewertung */}
          {note.personalRating && (
            <div className="flex items-center gap-0.5 mt-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className={`text-xs ${i < note.personalRating! ? 'text-amber-400' : 'text-gray-200'}`}
                >
                  ★
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Datum */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-1 text-xs text-gray-500">
        <Calendar className="w-3 h-3" />
        <span>
          {date.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </span>
      </div>
    </button>
  );
}

export default LogbookMemories;
